// Import necessary currency utilities
import { getCurrencyFromCountry } from '../../redux/slices/currencySlice';

/**
 * Format an amount from cents to dollars/euros
 * @param {number} amount - The amount in cents
 * @param {string} currency - The currency code (default: 'USD')
 * @returns {string} The formatted amount in dollars/euros
 */
export const formatAmount = (amount, currency = 'USD') => {
  if (amount === undefined || amount === null) return '0.00';
  
  // Convert from cents to dollars/euros
  const value = (amount / 100).toFixed(2);
  return value;
};

/**
 * Get currency symbol based on currency code
 * @param {string} currencyCode - The currency code (e.g., 'USD', 'EUR')
 * @returns {string} The currency symbol
 */
export const getCurrencySymbol = (currencyCode = 'USD') => {
  const symbols = {
    'USD': '$',
    'EUR': '€',
    'GBP': '£',
    'JPY': '¥',
    'CNY': '¥',
    'AUD': 'A$',
    'CAD': 'C$',
    'CHF': 'CHF',
    'HKD': 'HK$',
    'SGD': 'S$',
    'INR': '₹',
    'MXN': 'MX$',
    'BRL': 'R$',
    'KRW': '₩',
    'RUB': '₽',
    'ZAR': 'R'
  };
  
  return symbols[currencyCode.toUpperCase()] || '$';
};

/**
 * Extract available balance from Stripe Balance data
 * @param {Object} balanceData - Stripe balance data
 * @param {Object} user - User object that may contain country info
 * @returns {number} - Available balance as a number
 */
export const getAvailableBalance = (balanceData, user) => {
  if (!balanceData || !balanceData.balance) {
    return null;
  }

  const available = balanceData.balance.available;
  
  if (!available || !available.length) {
    return 0;
  }
  
  // Sum up all available balances across all currencies
  return available.reduce((sum, balance) => sum + (balance.amount / 100), 0);
};

/**
 * Extract pending balance from Stripe Balance data
 * @param {Object} balanceData - Stripe balance data
 * @param {Object} user - User object that may contain country info
 * @returns {number} - Pending balance as a number
 */
export const getPendingBalance = (balanceData, user) => {
  if (!balanceData || !balanceData.balance) {
    return null;
  }

  const pending = balanceData.balance.pending;
  
  if (!pending || !pending.length) {
    return 0;
  }
  
  // Sum up all pending balances across all currencies
  return pending.reduce((sum, balance) => sum + (balance.amount / 100), 0);
};

/**
 * Extract instant available balance from Stripe Balance data
 * @param {Object} balanceData - Stripe balance data
 * @returns {Object|null} - Instant available balance with amount and currency
 */
export const getInstantAvailableBalance = (balanceData) => {
  if (!balanceData || !balanceData.balance || !balanceData.balance.instant_available) {
    return null;
  }

  const instantAvailable = balanceData.balance.instant_available;
  
  if (!instantAvailable || !instantAvailable.length) {
    return null;
  }
  
  // Return the first instant available balance (usually only one)
  return {
    amount: instantAvailable[0].amount / 100,
    currency: instantAvailable[0].currency
  };
};

/**
 * Get the currency code from balance data or user's country
 * @param {Object} balanceData - The Stripe balance data
 * @param {Object} user - The user object that may contain country info
 * @returns {string} - The currency code (defaults to USD)
 */
export const getBalanceCurrency = (balanceData, user) => {
  // First check if we have available balance with currency info
  if (balanceData?.balance?.available && balanceData.balance.available.length > 0) {
    return balanceData.balance.available[0].currency.toUpperCase();
  }
  
  // Then check if we have pending balance with currency info
  if (balanceData?.balance?.pending && balanceData.balance.pending.length > 0) {
    return balanceData.balance.pending[0].currency.toUpperCase();
  }
  
  // If no balance data, use user's country to determine currency
  if (user?.country) {
    return getCurrencyFromCountry(user.country);
  }
  
  // Default to USD
  return 'USD';
};

/**
 * Format transaction amount with currency
 * @param {Object} transaction - Transaction object from Stripe
 * @returns {string} - Formatted amount string
 */
export const formatTransactionAmount = (transaction) => {
  if (!transaction || typeof transaction.amount === 'undefined') {
    // Return a default formatted string or an empty string if transaction or amount is invalid
    const formatter = new Intl.NumberFormat(navigator.language || 'en-US', {
      style: 'currency',
      currency: 'USD' // Default currency
    });
    return formatter.format(0);
  }
  
  const amount = transaction.amount / 100; // Amount is expected to be in cents
  const currencyCode = (transaction.currency && typeof transaction.currency === 'string') 
    ? transaction.currency.toUpperCase() 
    : 'USD'; // Default to USD if currency is missing or not a string
  
  const formatter = new Intl.NumberFormat(navigator.language || 'en-US', {
    style: 'currency',
    currency: currencyCode
  });
  
  return formatter.format(amount);
}; 